import React, { useMemo, useState } from 'react'
import MenuItem from './MenuItem'
import { parseKey } from '../utils/basketKeys'

// popular first, otherwise preserve the server's position order (stable sort)
function sortItems(items) {
  return [...items].sort((a, b) => (b.popular === true) - (a.popular === true))
}

// Renders the guest menu for a category.
//
// When offerFullMenu is off the server already sent only the current sitting's
// items, so this is just today's flat list. When it's on, every visible item is
// present and we split them into:
//   - featured: tagged with the current sitting (shown first, under the label)
//   - rest: everything else (behind a "show full menu" expander)
// plus a search box that filters name/description across the whole menu.
export default function MenuList({
  items,
  basket,
  onAdd,
  onRemove,
  sitting,
  sittingLabel,
  offerFullMenu,
  startCollapsed,
  emptyLabel
}) {
  const [query, setQuery] = useState('')
  const trimmed = query.trim().toLowerCase()
  const searching = trimmed.length > 0

  const { featured, rest } = useMemo(() => {
    if (!offerFullMenu) {
      return { featured: sortItems(items), rest: [] }
    }
    const feat = []
    const other = []
    items.forEach(item => {
      if (Array.isArray(item.sittings) && item.sittings.includes(sitting)) {
        feat.push(item)
      } else {
        other.push(item)
      }
    })
    return { featured: sortItems(feat), rest: sortItems(other) }
  }, [items, sitting, offerFullMenu])

  const searchResults = useMemo(() => {
    if (!searching) return []
    return items.filter(item => {
      const haystack = `${item.name} ${item.description || ''}`.toLowerCase()
      return haystack.includes(trimmed)
    })
  }, [items, searching, trimmed])

  // Collapse only makes sense when there's a genuine "rest" hidden behind
  // featured items. No featured items → show the whole menu flat (plan edge case).
  const collapsible = offerFullMenu && featured.length > 0 && rest.length > 0
  const [expanded, setExpanded] = useState(!startCollapsed)

  const renderItem = (item) => {
    const hasAddOns = Array.isArray(item.add_ons) && item.add_ons.length > 0
    // For add-on items the quantity spans every composite line for this item;
    // for plain items it's simply the bare-key quantity.
    const quantity = hasAddOns
      ? Object.entries(basket).reduce(
          (sum, [key, qty]) => (parseKey(key).menuItemId === item.id ? sum + qty : sum),
          0
        )
      : (basket[item.id] || 0)
    return (
      <MenuItem
        key={item.id}
        item={item}
        quantity={quantity}
        onAdd={onAdd}
        onRemove={onRemove}
      />
    )
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500" role="status">
        <p>{emptyLabel}</p>
      </div>
    )
  }

  // In full-menu mode the sitting label ("Lunch Menu") would mislabel a menu the
  // guest can order in full, so drop it — the featured items just sit at the top.
  // In sitting-gated mode the label is accurate and kept as today.
  const heading = offerFullMenu ? null : sittingLabel
  const primary = featured.length > 0 ? featured : rest

  return (
    <div>
      {offerFullMenu && (
        <div className="px-4 pt-2 pb-1">
          <label htmlFor="menu-search" className="sr-only">Search the menu</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0011.454 11.454z" />
              </svg>
            </div>
            <input
              id="menu-search"
              type="text"
              inputMode="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the menu…"
              className="block w-full rounded-lg border-gray-300 shadow-sm pl-10 pr-10 py-1.5 text-gray-900 focus:ring-sky-500 focus:border-sky-500"
            />
            {searching && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                <span aria-hidden="true" className="text-xl leading-none">&times;</span>
              </button>
            )}
          </div>
        </div>
      )}

      {searching ? (
        <div className="px-4 pt-2 space-y-3">
          {searchResults.length > 0 ? (
            searchResults.map(renderItem)
          ) : (
            <div className="text-center py-8 text-gray-500" role="status">
              <p>No items match &ldquo;{query}&rdquo;.</p>
            </div>
          )}
        </div>
      ) : (
        <>
          {heading && (
            <div className="px-4 pt-4 pb-1">
              <h2 className="text-lg font-semibold text-gray-100">{heading}</h2>
            </div>
          )}

          <div className="px-4 pt-2 space-y-3">
            {primary.map(renderItem)}
          </div>

          {collapsible && !expanded && (
            <div className="px-4 pt-4">
              <button
                type="button"
                onClick={() => setExpanded(true)}
                aria-expanded="false"
                aria-controls="full-menu-rest"
                className="w-full rounded-lg border border-gray-600 bg-white/5 py-3 text-sm font-medium text-gray-100 hover:bg-white/10"
              >
                Show full menu ({rest.length} more {rest.length === 1 ? 'item' : 'items'})
              </button>
            </div>
          )}

          {/* Kept mounted whenever there's a featured/rest split so the expander's
              aria-controls references a real element; hidden while collapsed. */}
          {featured.length > 0 && rest.length > 0 && (
            <div id="full-menu-rest" hidden={collapsible && !expanded}>
              <div className="px-4 pt-6 pb-1">
                <h2 className="text-lg font-semibold text-gray-100">Everything else</h2>
              </div>
              <div className="px-4 pt-2 space-y-3">
                {rest.map(renderItem)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
